import { useMemo } from "react";

import CircleIcon from "@mui/icons-material/Circle";
import {
  Box,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { RosterCell, RosterGroup } from "@/api/roster";
import type { Organization } from "@/api/types";
import { SlotKindChip } from "@/components/SlotKindChip";
import { slotIsOperating } from "@/lib/operatingHours";

/**
 * Phone rendering of the roster: one day, organizations stacked vertically.
 *
 * The desktop grid is orgs × days × slots, which cannot survive a 390 px
 * viewport — horizontal scrolling a matrix on a phone is unusable. Here the
 * day is fixed and each organization becomes a card, so the whole thing reads
 * as a single vertical scroll: "who is on duty, where, when".
 */

function rosterGroupToOrg(g: RosterGroup): Organization {
  return {
    operates_weekends: g.operates_weekends ?? true,
    operates_holidays: g.operates_holidays ?? true,
    operating_hours_start: g.operating_hours_start ?? null,
    operating_hours_end: g.operating_hours_end ?? null,
    closed_dates: g.closed_dates ?? [],
  } as unknown as Organization;
}

function hhmm(t: string): string {
  return t.slice(0, 5);
}

function minutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Is `now` inside [start, end)? Handles the overnight wrap. */
function isLive(start: string, end: string, nowMinutes: number): boolean {
  const s = minutes(start);
  const e = minutes(end);
  if (s === e) return false;
  return s < e ? nowMinutes >= s && nowMinutes < e : nowMinutes >= s || nowMinutes < e;
}

type Run = { cells: RosterCell[] };

/**
 * Merge a practitioner's contiguous slots into one line — same rule as the
 * desktop grid, so 8h-9h + 9h-10h reads as a single "08:00–10:00" entry.
 * Vacant slots stay separate: each unfilled tranche must be visible on its own.
 */
function buildRuns(cells: RosterCell[]): Run[] {
  const byPid = new Map<string, RosterCell[]>();
  for (const c of cells) {
    const key = c.practitioner_id ?? `__vacant__${c.slot_template_id}`;
    const arr = byPid.get(key) ?? [];
    arr.push(c);
    byPid.set(key, arr);
  }

  const runs: Run[] = [];
  for (const list of byPid.values()) {
    list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    if (list[0]!.practitioner_id == null) {
      for (const c of list) runs.push({ cells: [c] });
      continue;
    }
    let current: RosterCell[] = [];
    for (const c of list) {
      if (current.length === 0 || current[current.length - 1]!.end_time === c.start_time) {
        current.push(c);
      } else {
        runs.push({ cells: current });
        current = [c];
      }
    }
    if (current.length > 0) runs.push({ cells: current });
  }
  runs.sort((a, b) => a.cells[0]!.start_time.localeCompare(b.cells[0]!.start_time));
  return runs;
}

export function RosterMobileDay({
  date,
  groups,
  cells,
  highlightId,
  onlyHighlighted,
  isToday,
}: {
  date: string;
  groups: RosterGroup[];
  cells: RosterCell[];
  highlightId: string | null;
  onlyHighlighted: boolean;
  isToday: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;

  // "En cours" only makes sense while looking at the current day.
  const nowMinutes = useMemo(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, []);

  const cards = useMemo(() => {
    return groups
      .map((g) => {
        const orgConf = rosterGroupToOrg(g);
        const own = cells.filter(
          (c) =>
            c.organization_id === g.id &&
            slotIsOperating(orgConf, date, {
              start_time: c.start_time,
              end_time: c.end_time,
            }),
        );
        let runs = buildRuns(own);
        if (onlyHighlighted && highlightId) {
          runs = runs.filter((r) => r.cells[0]!.practitioner_id === highlightId);
        }
        return { group: g, runs };
      })
      .filter((c) => c.runs.length > 0);
  }, [groups, cells, date, onlyHighlighted, highlightId]);

  if (cards.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        {t("roster.mobileEmpty")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {cards.map(({ group, runs }) => (
        <Paper key={group.id} variant="outlined" sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: group.color
                ? alpha(group.color, mode === "dark" ? 0.35 : 0.55)
                : "action.hover",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {group.code}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {group.name}
            </Typography>
          </Box>

          <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
            {runs.map((run, i) => {
              const first = run.cells[0]!;
              const last = run.cells[run.cells.length - 1]!;
              const vacant = first.practitioner_id == null;
              const highlighted =
                highlightId != null && first.practitioner_id === highlightId;
              const live = isToday && isLive(first.start_time, last.end_time, nowMinutes);

              return (
                <Stack
                  key={`${first.slot_template_id}-${first.practitioner_id ?? "v"}-${i}`}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    bgcolor: highlighted
                      ? alpha(theme.palette.primary.main, mode === "dark" ? 0.25 : 0.12)
                      : undefined,
                  }}
                >
                  {/* Time column — fixed width so names align down the card. */}
                  <Box sx={{ minWidth: 92, flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 700, lineHeight: 1.3 }}
                    >
                      {hhmm(first.start_time)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      → {hhmm(last.end_time)}
                      {last.is_overnight ? " ⁺¹" : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {live && (
                        <CircleIcon
                          sx={{ fontSize: 9, color: "success.main", flexShrink: 0 }}
                          titleAccess={t("roster.liveNow")}
                        />
                      )}
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: vacant ? 400 : 600,
                          fontStyle: vacant ? "italic" : undefined,
                          color: vacant ? "text.secondary" : undefined,
                        }}
                      >
                        {vacant ? t("roster.toFill") : first.practitioner_name}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {first.slot_label}
                    </Typography>
                  </Box>

                  <SlotKindChip kind={first.slot_kind} />
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
